import React from 'react';
import { useTranslation } from 'react-i18next';
import { Puzzle, X } from 'lucide-react';

/**
 * Aviso único por usuário/versão quando há nova publicação de plugins Moodle.
 * Conteúdo espelha o changelog definido no painel Master.
 */
export default function PluginUpdateBalloon({ manifest, onDismiss, onOpenPlugins }) {
  const { t } = useTranslation('builder');
  const plugins = Array.isArray(manifest?.plugins) ? manifest.plugins : [];

  if (!plugins.length) return null;

  return (
    <div className="fixed top-4 right-4 z-[2000] animate-in slide-in-from-right duration-300 max-w-md w-[calc(100%-2rem)]">
      <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl border border-amber-200 dark:border-amber-800 overflow-hidden">
        <div className="h-1 bg-gradient-to-r from-amber-400 to-orange-400" />
        <div className="p-4">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 bg-amber-50 dark:bg-amber-950/50 text-amber-600 dark:text-amber-400 rounded-full flex items-center justify-center flex-shrink-0">
              <Puzzle size={20} />
            </div>
            <div className="flex-1 min-w-0">
              <h4 className="text-sm font-bold text-slate-800 dark:text-slate-100 mb-1">
                {t('pluginUpdateBalloonTitle')}
              </h4>
              <p className="text-xs text-slate-500 dark:text-slate-400 mb-3 leading-relaxed">
                {t('pluginUpdateBalloonDesc')}
              </p>
              <div className="space-y-3 max-h-[min(40vh,280px)] overflow-y-auto pr-1">
                {plugins.map((p) => (
                  <div
                    key={p.id}
                    className="rounded-lg border border-slate-100 dark:border-slate-700 bg-slate-50/80 dark:bg-slate-900/40 p-3"
                  >
                    <div className="text-xs font-semibold text-slate-800 dark:text-slate-100">
                      {p.title || p.id}
                      {p.version && String(p.version).trim() && p.version !== '—' ? (
                        <span className="font-normal text-slate-500 dark:text-slate-400">
                          {' '}
                          · {t('pluginVersionLabel')} {p.version}
                        </span>
                      ) : null}
                    </div>
                    {p.updated_at && String(p.updated_at).trim() ? (
                      <div className="text-[11px] text-slate-400 dark:text-slate-500 mt-0.5">
                        {t('pluginUpdatedLabel')}: {p.updated_at}
                      </div>
                    ) : null}
                    {p.changelog && String(p.changelog).trim() ? (
                      <pre className="mt-2 text-[11px] leading-relaxed whitespace-pre-wrap font-sans text-slate-600 dark:text-slate-300">
                        {p.changelog}
                      </pre>
                    ) : null}
                  </div>
                ))}
              </div>
              <div className="flex flex-wrap gap-2 mt-4">
                <button
                  type="button"
                  onClick={onOpenPlugins}
                  className="px-3 py-1.5 text-xs font-semibold rounded-lg bg-blue-600 text-white hover:bg-blue-700 dark:bg-blue-600 dark:hover:bg-blue-500"
                >
                  {t('pluginUpdateBalloonOpen')}
                </button>
                <button
                  type="button"
                  onClick={onDismiss}
                  className="px-3 py-1.5 text-xs font-semibold rounded-lg text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700"
                >
                  {t('pluginUpdateBalloonDismiss')}
                </button>
              </div>
            </div>
            <button
              type="button"
              onClick={onDismiss}
              className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 flex-shrink-0"
              aria-label={t('pluginUpdateBalloonDismiss')}
            >
              <X size={16} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
